import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { getProgramsByDistrict } from '../../data/educationData';
import './DistrictDetail.css';

const DistrictDetail = () => {
    useScrollAnimation();
    const { districtId } = useParams();
    const navigate = useNavigate();
    
    const districtData = getProgramsByDistrict(districtId);

    if (!districtData) {
        return (
            <div className="district-detail-page">
                <div className="container">
                    <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        <h2>District Not Found</h2>
                        <p>The district you're looking for doesn't exist or doesn't have active programs yet.</p>
                        <button onClick={() => navigate('/programs/education')} className="btn btn-primary">
                            Back to Education Programs
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const { name, tamilName, programs, stats } = districtData;

    return (
        <div className="district-detail-page">
            {/* Hero Section */}
            <div className="district-hero">
                <div className="container">
                    <button onClick={() => navigate('/programs/education')} className="back-link">
                        <i className="fas fa-arrow-left"></i> Back to Education Programs
                    </button>
                    <div className="district-hero-content">
                        <div className="district-icon">
                            <i className="fas fa-map-marker-alt"></i>
                        </div>
                        <h1>{name} ({tamilName})</h1>
                        <p className="district-tagline">Education Programs in {name}</p>
                    </div>
                </div>
            </div>

            {/* Statistics Overview */}
            <div className="district-stats-section">
                <div className="container">
                    <div className="stats-grid scroll-animate">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-users"></i>
                            </div>
                            <div className="stat-content">
                                <div className="stat-number">{stats.totalStudents}+</div>
                                <div className="stat-label">Students Enrolled</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-hands-helping"></i>
                            </div>
                            <div className="stat-content">
                                <div className="stat-number">{stats.totalVolunteers}</div>
                                <div className="stat-label">Volunteers</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-building"></i>
                            </div>
                            <div className="stat-content">
                                <div className="stat-number">{stats.totalCenters}</div>
                                <div className="stat-label">Learning Centers</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <i className="fas fa-calendar-alt"></i>
                            </div>
                            <div className="stat-content">
                                <div className="stat-number">{stats.yearsActive}</div>
                                <div className="stat-label">Years Active</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Programs Section */}
            <div className="programs-section">
                <div className="container">
                    {programs.map((program, index) => (
                        <div key={program.id} className="program-detail-card scroll-animate">
                            <div className="program-header">
                                <div className="program-title-group">
                                    <h2>{program.name}</h2>
                                    <p className="tamil-name">{program.tamilName}</p>
                                </div>
                                <div className="program-badge">
                                    <i className="fas fa-check-circle"></i> Active
                                </div>
                            </div>

                            <div className="program-info-grid">
                                <div className="program-description">
                                    <p>{program.description}</p>
                                </div>

                                <div className="program-quick-info">
                                    <div className="info-item">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <div>
                                            <strong>Location</strong>
                                            <p>{program.location}</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-clock"></i>
                                        <div>
                                            <strong>Timings</strong>
                                            <p>{program.timings}</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-users"></i>
                                        <div>
                                            <strong>Enrolled</strong>
                                            <p>{program.studentsEnrolled} Students</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-hands-helping"></i>
                                        <div>
                                            <strong>Volunteers</strong>
                                            <p>{program.volunteers} Active</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Subjects */}
                            <div className="program-subjects">
                                <h3><i className="fas fa-book"></i> Subjects Taught</h3>
                                <div className="subjects-tags">
                                    {program.subjects.map((subject, i) => (
                                        <span key={i} className="subject-tag">{subject}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Achievements */}
                            <div className="program-achievements">
                                <h3><i className="fas fa-trophy"></i> Key Achievements</h3>
                                <ul className="achievements-list">
                                    {program.achievements.map((achievement, i) => (
                                        <li key={i}>
                                            <i className="fas fa-star"></i> {achievement}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Images */}
                            {program.images && program.images.length > 0 && (
                                <div className="program-gallery">
                                    <h3><i className="fas fa-images"></i> Program Photos</h3>
                                    <div className="gallery-grid">
                                        {program.images.map((image, i) => (
                                            <div key={i} className="gallery-item">
                                                <img 
                                                    src={image} 
                                                    alt={`${program.name} ${i + 1}`}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/400x300?text=Program+Photo';
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contact */}
                            <div className="program-contact">
                                <h3><i className="fas fa-address-card"></i> Contact Information</h3>
                                <div className="contact-details">
                                    <div className="contact-item">
                                        <i className="fas fa-user"></i>
                                        <div>
                                            <strong>Coordinator</strong>
                                            <p>{program.contact.coordinator}</p>
                                        </div>
                                    </div>
                                    <div className="contact-item">
                                        <i className="fas fa-phone"></i>
                                        <div>
                                            <strong>Phone</strong>
                                            <p>{program.contact.phone}</p>
                                        </div>
                                    </div>
                                    <div className="contact-item">
                                        <i className="fas fa-envelope"></i>
                                        <div>
                                            <strong>Email</strong>
                                            <p>{program.contact.email}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA Section */}
            <div className="district-cta">
                <div className="container">
                    <div className="cta-content scroll-animate">
                        <h2>Support Education in {name}</h2>
                        <p>Your contribution can help us reach more children and provide quality education</p>
                        <div className="cta-buttons">
                            <button onClick={() => navigate('/donate')} className="btn btn-primary btn-large">
                                <i className="fas fa-heart"></i> Donate Now
                            </button>
                            <button onClick={() => navigate('/contact')} className="btn btn-secondary btn-large">
                                <i className="fas fa-hands-helping"></i> Volunteer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DistrictDetail;
